import React from "react";
import styled from "@emotion/styled";
import { Skeleton } from "antd5";

import { generateNoticeSearchUrl } from "components/notices/utils";
import {
  convertOpportunityFiltersToSearchRequest,
  DEFAULT_PAGINATION,
  EMPTY_OPPORTUNITY_FILTERS,
  generateOpportunitiesSearchUrl,
} from "components/opportunities/utils";
import Card from "components/ui/card/Card";
import Modal from "components/ui/modal/Modal";
import { useOpportunities } from "lib/hooks/api/opportunities/useOpportunities";
import { useOpportunityWorkflow } from "lib/hooks/api/opportunities/useOpportunityWorkflow";
import UIcon from "lib/icons/untitled_ui/UIcon";
import { EventNames, useTracking } from "lib/tracking";
import { Flex, Text } from "../../styles/utility-components";

import BID_STUDIO_DEMO_GIF from "../../../assets/images/bid_module_demo.gif";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  openOpportunityDrawer: () => void;
};
function CreateBidModal({ open, onOpenChange, openOpportunityDrawer }: Props): JSX.Element {
  const { logEvent } = useTracking();
  const { data, isLoading } = useOpportunities(
    convertOpportunityFiltersToSearchRequest(EMPTY_OPPORTUNITY_FILTERS),
    DEFAULT_PAGINATION,
    { enabled: open },
  );

  const { data: workflowTransitions } = useOpportunityWorkflow({ enabled: open });

  const qualifyStageIds =
    workflowTransitions?.transitions
      .filter((t) => t.group !== "Pre_Engagement")
      .map((t) => t.stage.id) || [];

  const trackCardClick = (option: string) => {
    logEvent(EventNames.bidReportOptionSelected, {
      "Context source": "Bid studio",
      Option: option,
    });
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <Content>
        <Flex flexDirection="column" gap={32}>
          <Flex flexDirection="column" gap={24} alignItems="flex-start">
            <Flex gap={8} alignItems="center">
              <UIcon icon="bidPart" size={20} /> <Text h3>Win bids</Text>
            </Flex>
            <Flex flexDirection="column" gap={8}>
              <Text h1>Create a bid / no-bid report</Text>
              <Description fontWeight="500">
                Bid / no-bid reports pull out what really matters from tender documents, so you can
                focus on opportunities worth your time.
              </Description>
            </Flex>
          </Flex>
          <Flex flexDirection="column" gap={16}>
            {isLoading ? (
              <>
                <Skeleton active />
                <Skeleton active />
                <Skeleton active />
              </>
            ) : (
              <>
                <Card
                  onClick={() => trackCardClick("Stotles Tender")}
                  // TODO: Make this an SPA link when MyFeedPage is linked that way
                  href={generateNoticeSearchUrl({}, window.settings?.openOppsViewId)}
                >
                  <Text h3>Find an open tender</Text>
                  <Description>
                    I want to find a new tender to qualify for a bid / no-bid decision
                  </Description>
                </Card>
                {data && data.total > 0 && (
                  <Card
                    href={generateOpportunitiesSearchUrl({
                      stageIds: qualifyStageIds,
                    })}
                    onClick={() => trackCardClick("Opportunity")}
                  >
                    <Text h3>Progress an existing opportunity</Text>
                    <Description>
                      I want to make a bid / no-bid decision for an existing opportunity
                    </Description>
                  </Card>
                )}
                <Card
                  onClick={() => {
                    trackCardClick("External Tender");
                    openOpportunityDrawer();
                  }}
                >
                  <Text h3>Create from a tender outside of Stotles</Text>
                  <Description>
                    I've been invited to tender and I want to make a bid / no-bid decision
                  </Description>
                </Card>
              </>
            )}
          </Flex>
        </Flex>

        <GifImage src={BID_STUDIO_DEMO_GIF} alt="Bid Studio Demo" />
      </Content>
    </Modal>
  );
}

const Content = styled(Flex)({
  padding: 60,
  alignItems: "center",
  gap: 48,
  "@media (max-width: 1400px)": {
    padding: 24,
  },
  overflow: "hidden",
});

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
}));

const GifImage = styled.img({
  borderRadius: "8px",
  maxWidth: "900px",
  "@media (max-width: 1600px)": {
    maxWidth: "650px",
  },

  "@media (max-width: 1200px)": {
    maxWidth: "500px",
  },
});

export default CreateBidModal;
